/**
 * Jalali (Shamsi/Persian) ⇄ Gregorian date conversion — pure integer date math, zero dependencies.
 *
 * Storage stays ISO Gregorian everywhere (ARCHITECTURE §5: unambiguous storage); this converts ONLY at the
 * UI edge so Iranian operators see/enter Persian dates. Display of Jalali already works via `Intl('fa-IR')`;
 * the missing half is INPUT (a `<input type=date>` is Gregorian), which needs a real Jalali↔Gregorian
 * algorithm — implemented here from the well-known, public-domain `jalaali-js` (Behrang Noruzi Niya).
 */
const div = (a, b) => Math.trunc(a / b);
const mod = (a, b) => a - Math.trunc(a / b) * b;
/** Leap/reference info for a Jalali year (jalaali-js `jalCal`). `leap` = years since the last leap year. */
function jalCal(jy) {
    const breaks = [
        -61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394,
        2456, 3178,
    ];
    const gy = jy + 621;
    let leapJ = -14;
    let jp = breaks[0];
    let jump = 0;
    for (let i = 1; i < breaks.length; i += 1) {
        const jm = breaks[i];
        jump = jm - jp;
        if (jy < jm)
            break;
        leapJ += div(jump, 33) * 8 + div(mod(jump, 33), 4);
        jp = jm;
    }
    let n = jy - jp;
    leapJ += div(n, 33) * 8 + div(mod(n, 33) + 3, 4);
    if (mod(jump, 33) === 4 && jump - n === 4)
        leapJ += 1;
    const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150;
    const march = 20 + leapJ - leapG;
    if (jump - n < 6)
        n = n - jump + div(jump + 4, 33) * 33;
    let leap = mod(mod(n + 1, 33) - 1, 4);
    if (leap === -1)
        leap = 4;
    return { leap, gy, march };
}
/** Gregorian (y,m,d) → Julian Day Number. */
function g2d(gy, gm, gd) {
    let d = div((gy + div(gm - 8, 6) + 100100) * 1461, 4) +
        div(153 * mod(gm + 9, 12) + 2, 5) +
        gd -
        34840408;
    d = d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752;
    return d;
}
/** Julian Day Number → Gregorian (y,m,d). */
function d2g(jdn) {
    let j = 4 * jdn + 139361631;
    j += div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908;
    const i = div(mod(j, 1461), 4) * 5 + 308;
    const gd = div(mod(i, 153), 5) + 1;
    const gm = mod(div(i, 153), 12) + 1;
    const gy = div(j, 1461) - 100100 + div(8 - gm, 6);
    return { gy, gm, gd };
}
/** Jalali (y,m,d) → Julian Day Number. */
function j2d(jy, jm, jd) {
    const r = jalCal(jy);
    return g2d(r.gy, 3, r.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1;
}
/** Julian Day Number → Jalali (y,m,d). */
function d2j(jdn) {
    const gy = d2g(jdn).gy;
    let jy = gy - 621;
    const r = jalCal(jy);
    const jdn1f = g2d(gy, 3, r.march);
    let k = jdn - jdn1f;
    if (k >= 0) {
        if (k <= 185)
            return { jy, jm: 1 + div(k, 31), jd: mod(k, 31) + 1 };
        k -= 186;
    }
    else {
        jy -= 1;
        k += 179;
        if (r.leap === 1)
            k += 1;
    }
    return { jy, jm: 7 + div(k, 30), jd: mod(k, 30) + 1 };
}
export function toJalali(gy, gm, gd) {
    return d2j(g2d(gy, gm, gd));
}
export function toGregorian(jy, jm, jd) {
    return d2g(j2d(jy, jm, jd));
}
/** True if `jy` is a leap Jalali year (Esfand has 30 days). */
export function isLeapJalali(jy) {
    return jalCal(jy).leap === 0;
}
/** Days in Jalali month `jm` of year `jy`: 31 (months 1–6), 30 (7–11), 29/30 (Esfand). */
export function jalaliMonthLength(jy, jm) {
    if (jm <= 6)
        return 31;
    if (jm <= 11)
        return 30;
    return isLeapJalali(jy) ? 30 : 29;
}
export const JALALI_MONTHS = [
    'فروردین',
    'اردیبهشت',
    'خرداد',
    'تیر',
    'مرداد',
    'شهریور',
    'مهر',
    'آبان',
    'آذر',
    'دی',
    'بهمن',
    'اسفند',
];
/** Weekday labels, Saturday-first (the Iranian week). Index by `saturdayFirstDow(iso)`. */
export const JALALI_WEEKDAYS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
const faDigit = (s) => s.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
const iso3 = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.slice(0, 10));
    return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
};
/** ISO `YYYY-MM-DD` → Jalali parts, or null if the ISO is unparseable. */
export function isoToJalali(iso) {
    const g = iso3(iso);
    return g ? toJalali(g[0], g[1], g[2]) : null;
}
/** Jalali parts → ISO `YYYY-MM-DD`. */
export function jalaliToIso(jy, jm, jd) {
    const g = toGregorian(jy, jm, jd);
    return `${String(g.gy).padStart(4, '0')}-${String(g.gm).padStart(2, '0')}-${String(g.gd).padStart(2, '0')}`;
}
/** ISO → «۱۴۰۵/۰۴/۱۶» (Persian digits), or '' if unparseable. */
export function formatJalali(iso) {
    if (!iso)
        return '';
    const j = isoToJalali(iso);
    if (!j)
        return '';
    return faDigit(`${j.jy}/${String(j.jm).padStart(2, '0')}/${String(j.jd).padStart(2, '0')}`);
}
/** Day-of-week for an ISO date, Saturday=0 … Friday=6 (the Iranian week; JS getUTCDay has Sunday=0). */
export function saturdayFirstDow(iso) {
    const g = iso3(iso);
    if (!g)
        return 0;
    return (new Date(Date.UTC(g[0], g[1] - 1, g[2])).getUTCDay() + 1) % 7;
}
//# sourceMappingURL=jalali.js.map